import { useState } from "react";
import {
  Form,
  Input,
  Button,
  Typography,
  Checkbox,
  message,
  Modal,
} from "antd";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { REACT_APP_API_TENANTS_URL } from "../../constants";

const { Title, Text } = Typography;

const CreateNewUserTenant = () => {
  const [notification, setNotification] = message.useMessage();
  const [formData, setFormData] = useState();
  const [showPassword, setShowPassword] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const navigate = useNavigate();
  const [form] = Form.useForm();
  const showSuccessModal = () => {
    Modal.success({
      title: "User successfully created!",
      content:
        "We've sent an email with the login link and setup instructions. Please check your inbox to proceed.",
      onOk() {
        // Reset the form fields when user clicks "OK"
        form.resetFields();
        setIsSubmitting(false);
      },
    });
  };

  const handleCreateTenant = (values) => {
    setIsSubmitting(true);
    const data = {
      email: values.email,
      password: values.password,
    };

    axios
      .post(`${REACT_APP_API_TENANTS_URL}/createtenantV1`, data, {
        headers: {
          "Content-Type": "application/json",
          Cookie:
            "route=1745835858.5.176.298168|8b9872d7e84b9204ed00a10a367344a2",
          withCredentials: true,
        },
      })
      .then((res) => {
        setTimeout(() => {
          showSuccessModal();
        }, 1000);
      })
      .catch((err) => {
        const errorMessage =
          err.response?.data?.message ||
          "Unable to create tenant. Please try again later.";
        notification.open({
          type: "error",
          content: errorMessage,
          onOk: () => setIsSubmitting(false),
        });
      });
  };

  const onFinishFailed = (errorInfo) => {
    console.log("Failed:", errorInfo);
  };

  const handlePasswordVisibility = (e) => {
    setShowPassword(e.target.checked);
  };
  return (
    <div className="h-screen bg-white flex overflow-hidden">
      {setNotification}

      {/* Left side - Form */}
      <div className="w-full lg:w-1/2 flex items-center justify-center p-8 lg:p-24 overflow-hidden">
        <div className="w-full max-w-md">
          <div className="mb-4">
            <Title level={2} className="text-3xl font-bold mb-3 text-gray-900">
              Register
            </Title>
            <Text className="text-gray-500 text-sm">
              Fill in the details below to get started
            </Text>
          </div>

          <Form
            form={form}
            name="create_tenant"
            initialValues={{ remember: true }}
            onFinish={handleCreateTenant}
            onFinishFailed={onFinishFailed}
            layout="vertical"
            requiredMark={false}
            fields={formData}
            size="large"
            className="space-y-6"
          >
            {/* Email Input */}
            <Form.Item
              name="email"
              label={
                <span className="text-gray-700 font-medium">Email address</span>
              }
              rules={[
                { required: true, message: "Please enter your email" },
                { type: "email", message: "Please enter a valid email" },
              ]}
            >
              <Input
                placeholder="you@example.com"
                className="h-12  border-gray-300 hover:border-violet-400 focus:border-violet-500 transition-colors"
              />
            </Form.Item>

            {/* Password Input */}
            <Form.Item
              name="password"
              label={
                <span className="text-gray-700 font-medium">Password</span>
              }
              rules={[
                { required: true, message: "Please enter a password" },
                { min: 8, message: "Password must be at least 8 characters" },
              ]}
            >
              <Input
                type={showPassword ? "text" : "password"}
                placeholder="Create a password"
                className="h-12  border-gray-300 hover:border-violet-400 focus:border-violet-500 transition-colors"
              />
            </Form.Item>

            {/* Confirm Password Input */}
            <Form.Item
              name="confirmPassword"
              label={
                <span className="text-gray-700 font-medium">
                  Confirm password
                </span>
              }
              dependencies={["password"]}
              rules={[
                { required: true, message: "Please confirm your password" },
                ({ getFieldValue }) => ({
                  validator(_, value) {
                    if (!value || getFieldValue("password") === value) {
                      return Promise.resolve();
                    }
                    return Promise.reject(
                      new Error("The two passwords don't match")
                    );
                  },
                }),
              ]}
            >
              <Input
                type={showPassword ? "text" : "password"}
                placeholder="Confirm your password"
                className="h-12  border-gray-300 hover:border-violet-400 focus:border-violet-500 transition-colors"
              />
            </Form.Item>

            {/* Show Password Checkbox */}
            <Form.Item className="mb-8">
              <Checkbox onChange={handlePasswordVisibility}>
                Show password
              </Checkbox>
            </Form.Item>

            {/* Submit Button */}
            <Form.Item>
              <Button
                type="primary"
                htmlType="submit"
                className="w-full h-12 rounded-lg bg-gradient-to-r transition-all duration-300"
                disabled={isSubmitting}
              >
                <span className="flex items-center justify-center text-base font-medium">
                  Register
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-5 w-5 ml-2"
                    viewBox="0 0 20 20"
                    fill="currentColor"
                  >
                    <path
                      fillRule="evenodd"
                      d="M12.293 5.293a1 1 0 011.414 0l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414-1.414L14.586 11H3a1 1 0 110-2h11.586l-2.293-2.293a1 1 0 010-1.414z"
                      clipRule="evenodd"
                    />
                  </svg>
                </span>
              </Button>
            </Form.Item>
          </Form>
        </div>
      </div>

      {/* Right side - Visual */}
      <div className="hidden lg:block lg:w-1/2 bg-gradient-to-br from-violet-500 via-purple-500 to-indigo-600 relative overflow-hidden">
        {/* Abstract shapes */}
        <div className="absolute top-0 left-0 w-full h-full">
          <div className="absolute top-[10%] right-[20%] w-64 h-64 rounded-full bg-white opacity-10"></div>
          <div className="absolute top-[40%] left-[10%] w-80 h-80 rounded-full bg-white opacity-10"></div>
          <div className="absolute bottom-[10%] right-[30%] w-72 h-72 rounded-full bg-white opacity-10"></div>

          {/* Mesh gradient overlay */}
          <div className="absolute inset-0 bg-gradient-to-br from-violet-600/30 via-transparent to-indigo-600/30"></div>
        </div>

        {/* Content */}
        <div className="relative z-10 flex flex-col justify-center h-full px-16">
          <div className="bg-white/10 backdrop-blur-md rounded-xl p-8 border border-white/20 max-w-md">
            <div className="text-white text-lg font-medium mb-4">
              Trusted by thousands
            </div>
            <p className="text-white/90 text-xl mb-6">
              "This platform has transformed how we manage authentication across
              our organization. The setup was seamless and the security features
              are exceptional."
            </p>
            <div className="flex items-center">
              <div className="w-10 h-10 rounded-full bg-white/20 flex items-center justify-center text-white font-medium">
                JD
              </div>
              <div className="ml-3"></div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CreateNewUserTenant;
